import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { SeoHead } from '../components/SeoHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const ContattiPage = () => {
  const { settings } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Per favore compila tutti i campi obbligatori');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Messaggio inviato con successo! Ti risponderemo presto.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Errore nell'invio del messaggio. Riprova pi\u00f9 tardi.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: 'Indirizzo', content: settings.address, link: settings.maps_url },
    { icon: Phone, title: 'Telefono', content: settings.phone, link: `tel:${settings.phone.replace(/\s/g, '')}` },
    { icon: Mail, title: 'Email', content: settings.email_address, link: `mailto:${settings.email_address}` },
    { icon: Clock, title: 'Orari', content: settings.orari, link: null },
  ];

  return (
    <div data-testid="contatti-page">
      <SeoHead
        title="Contatti | Azienda Agricola Le Bontà del Vesuvio, Ercolano (NA)"
        description="Contatta l'Azienda Agricola Le Bontà del Vesuvio: Via ex Cook 42, Ercolano (NA) 80056. Tel. +39 338 252 3925. Acquista pomodorini napoletani, conserve e prodotti tipici campani."
        keywords="contatti bontà del vesuvio, azienda agricola ercolano napoli, dove acquistare prodotti del vesuvio, telefono pomodorini napoletani"
        canonical="/contatti"
      />

      {/* Hero */}
      <section className="relative flex items-center" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src={settings.contatti_hero_image_url}
            alt="Contattaci"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">
              Parliamone
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white max-w-3xl">
              Contattaci
            </h1>
            <p className="font-body text-sm text-white/80 mt-2 max-w-xl">
              Siamo sempre disponibili per rispondere alle tue domande e per aiutarti 
              a scoprire i nostri prodotti.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-spacing" data-testid="contact-info">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl border border-primary/10 shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-DEFAULT mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="font-body text-text-muted hover:text-primary transition-colors"
                    data-testid={`contact-${info.title.toLowerCase()}`}
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="font-body text-text-muted">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-12 bg-[#25D366]" data-testid="whatsapp-cta">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <MessageCircle className="w-10 h-10 text-white" fill="white" />
              <div>
                <h3 className="font-heading text-xl font-semibold text-white">
                  Preferisci WhatsApp?
                </h3>
                <p className="font-body text-white/90">
                  Rispondiamo velocemente ai tuoi messaggi!
                </p>
              </div>
            </div>
            <a
              href={`https://wa.me/${settings.whatsapp_number}?text=Ciao!%20Vorrei%20informazioni%20sui%20vostri%20prodotti.`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta-btn"
            >
              <Button className="bg-white text-[#25D366] hover:bg-background-paper rounded-full px-8 py-6 font-body font-medium">
                Scrivici su WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-spacing bg-background-paper" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-secondary font-body text-sm uppercase tracking-widest mb-4 block">
                Inviaci un Messaggio
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-DEFAULT mb-8">
                Modulo di Contatto
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="font-body text-text-DEFAULT mb-2 block">
                      Nome *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Il tuo nome"
                      className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary bg-white"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-body text-text-DEFAULT mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="La tua email"
                      className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary bg-white"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="font-body text-text-DEFAULT mb-2 block">
                    Telefono (opzionale)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Il tuo numero di telefono"
                    className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary bg-white"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="font-body text-text-DEFAULT mb-2 block">
                    Messaggio *
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Come possiamo aiutarti?"
                    rows={5}
                    className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary resize-none bg-white"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary-dark text-white rounded-full py-6 font-body font-medium btn-primary"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? 'Invio in corso...' : 'Invia Messaggio'}
                </Button>
              </form>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="h-full min-h-[400px]"
            >
              <span className="text-secondary font-body text-sm uppercase tracking-widest mb-4 block">
                Dove Siamo
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-DEFAULT mb-8">
                Vieni a Trovarci
              </h2>
              <div className="rounded-xl overflow-hidden h-[400px] border border-primary/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.2!2d14.35!3d40.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ4JzAwLjAiTiAxNMKwMjEnMDAuMCJF!5e0!3m2!1sit!2sit!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mappa Azienda Agricola Le Bontà del Vesuvio"
                  data-testid="google-map"
                />
              </div>
              <div className="mt-6 p-4 bg-white rounded-xl border border-primary/10">
                <p className="font-body text-text-muted text-sm">
                  <strong className="text-text-DEFAULT">Indicazioni:</strong> L'azienda si trova 
                  a Ercolano, alle pendici del Vesuvio. Facilmente raggiungibile dalla 
                  A3 Napoli-Salerno, uscita Ercolano.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-16" data-testid="social-section">
        <div className="container-custom text-center">
          <h3 className="font-heading text-2xl font-semibold text-text-DEFAULT mb-6">
            Seguici sui Social
          </h3>
          <a
            href={settings.facebook_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#1877F2] text-white rounded-full font-body font-medium hover:bg-[#1664d9] transition-colors"
            data-testid="facebook-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Seguici su Facebook
          </a>
        </div>
      </section>
    </div>
  );
};
